import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux'
import {bindActionCreators} from 'redux';
import { triggerTransition } from '@uirouter/redux';

import Cart from '../components/Cart';
import { setCheckoutOverlay, clearAlerts, setShowPyosPicker, setShowBestAvailablePicker } from '../../../actions/ui';
import { deleteReservationWrapper } from '../../../actions/reservation';
import { createLoadingSelector } from 'checkout/selectors/helpers';
import { getOrderTotal } from 'checkout/selectors/cart';
import { getAdditionalFees, getEventIdsFromReservedItems, numberOfItemsInCart } from 'checkout/selectors/reservation';
import { getUpsoldProductsByListingUpsellId } from '../../product-picker/selectors/products';
import { updateProductQuantities } from '../../../actions/product-sales';
import { isSeatedListing, isPyos, getReserveCouponUsage } from '../../../selectors/reserved-seating';
import {isMultiEmbed, selectEventNameFromListingConfig, shouldHideEmbeddedCart, shouldPinEmbeddedCart} from '../../../selectors/ui';
import { TranslationContext } from "../../../config/Translations";
import { EMBED_CART_BEHAVIOR_PIN_TO_TOP, EMBED_CART_BEHAVIOR_PIN_TO_BOTTOM } from '../../../reducers/ui';
import { getMainEvent, hasCoupons } from '../../../selectors/event';
import { saveCurrentChildEvent } from '@b/checkout/actions/event';
import { secondsTohhmmss } from '@b/helpers/time';
import { isDynamicListing } from '@/selectors/dynamic-listing';

class CartContainer extends PureComponent {
    render() {
        return(
            <TranslationContext.Consumer>
            {translation =>
                <Cart
                    goToCheckout={this.onGoToCheckout}
                    clearCart={() => {this.onClearCart(translation)}}
                    closeCart={this.onCartClose}
                    total={this.props.total.amount}
                    fees={this.props.fees ? this.props.fees : []}
                    listings={this.props.listings}
                    deleteLoading={this.props.deleteLoading}
                    fetchLoading={this.props.fetchLoading}
                    hasCoupons={this.props.hasCoupons}
                    hasAutoCoupons={this.props.hasAutoCoupons}
                    onShowDiscountCoupon={this.showDiscountCoupon}
                    showSuccess={this.props.showSuccess}
                    coupon={this.props.coupon}
                    currencySymbol={this.props.currencySymbol}
                    eventIds={this.props.eventIds}
                    onAddTickets={this.onAddTickets}
                    upsoldProducts={this.props.upsoldProducts}
                    onUpdateProductQuantities={this.props.updateProductQuantities}
                    token={this.props.token}
                    secret={this.props.secret}
                    loadingStates={this.props.loadingStates}
                    canAddMoreTickets={!!this.props.isPyos || !this.props.isSeatedListing}
                    isPyos={!!this.props.isPyos}
                    isEmpty={this.props.isEmpty}
                    reservedCoupon={this.props.reservedCoupon}
                    isMultiEmbed={this.props.isMultiEmbed}
                    eventName={this.props.eventName}
                    isSeries={this.props.isSeries}
                    time={this.props.time}
                    isDynamic={this.props.isDynamicListing}
                />
            }
            </TranslationContext.Consumer>
        )
    }

    onGoToCheckout = () => {
        this.props.clearAlerts();
        this.props.triggerTransition("checkout", {token: this.props.token});
    };

    onAddTickets = () => {
        this.props.clearAlerts();
        if (this.props.isPyos) {
            this.props.setShowPyosPicker(true);
            this.props.setShowBestAvailablePicker(false);
        }
        const page = this.props.isSeries && !this.props.isDynamic ? "seriesDatePicker" : "ticketPicker";
        this.props.saveCurrentChildEvent();
        this.props.triggerTransition(page, {listing: this.props.listing});
        if (this.props.isDynamicListing) {
            this.props.setCheckoutOverlay(false);
        }
    }

    onClearCart = translation => {
        if (!this.props.isEmbed || confirm(translation.t("checkout__close_cart_confirmation"))) {
            this.props.clearAlerts();
            this.props.deleteReservationWrapper({
              reason: 'clearCart'
            });
            window.scrollTo(0, 0); // Scroll Listing to top of the page after clearing cart
        }
    };

    showDiscountCoupon = () => {
        this.props.clearAlerts();
        this.props.triggerTransition('coupon');
    };

    onCartClose = () => {
      this.props.triggerTransition('ticketPicker');
      this.props.setCheckoutOverlay(false);
    }
}

const deleteLoading = createLoadingSelector("RESERVATION_DELETE");
const fetchLoading = createLoadingSelector("RESERVATION_FETCH")
const mapStateToProps = state => {
    // get event IDs from all reserved inventory items as well as the event ID related to the listing
    const reservedIds = getEventIdsFromReservedItems(state)
    const eventIds = state.event.childEvent?.event_id ? [parseInt(state.event.childEvent.event_id)] : Object.keys(state.event.events).map(id => parseInt(id));
    const allEventIds = [...new Set([...reservedIds, ...eventIds])];
    const reservedCouponUsage = getReserveCouponUsage(state);
    const reservedCoupon = (!!reservedCouponUsage?.attributes?.coupon_link_id && (numberOfItemsInCart(state)>=reservedCouponUsage?.attributes?.ticket_limit));

    return {
        fetchLoading: fetchLoading(state),
        isMultiEmbed: isMultiEmbed(state),
        eventName: selectEventNameFromListingConfig(state),
        time: secondsTohhmmss(state.reservation.time_remaining),
        token: state.reservation.token,
        secret: state.reservation.secret,
        listing: state.event.listing,
        fees: getAdditionalFees(state),
        listings: state.reservation.listings,
        hasCoupons: hasCoupons(state),
        hasAutoCoupons: (state.reservation.autoCouponAmount !== null),
        showSuccess: state.coupon.showSuccess,
        coupon: state.coupon,
        currencySymbol: state.reservation.currency_symbol,
        deleteLoading: deleteLoading(state),
        reservationDeliveryFee: state.reservation.total_delivery_fees,
        reservationSelectedDelivery: state.reservation.selected_delivery_option,
        eventIds: allEventIds,
        total: getOrderTotal(state),
        upsoldProducts: getUpsoldProductsByListingUpsellId(state),
        loadingStates: state.loading.states,
        isSeatedListing: isSeatedListing(state),
        isPyos: isPyos(state),
        isEmpty: numberOfItemsInCart(state) < 1,
        isEmbed: !!state.ui.isEmbed,
        shouldPinEmbeddedCart: shouldPinEmbeddedCart(state),
        shouldHideEmbeddedCart: shouldHideEmbeddedCart(state),
        reservedCoupon,
        isSeries: !!getMainEvent(state)?.event?.isRecurring,
        isDynamicListing: isDynamicListing(state)
    }
};

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        triggerTransition,
        setCheckoutOverlay,
        deleteReservationWrapper,
        clearAlerts,
        updateProductQuantities,
        setShowPyosPicker,
        setShowBestAvailablePicker,
        saveCurrentChildEvent
    }, dispatch);
};

CartContainer.propTypes = {
    token: PropTypes.string.isRequired,
    listing: PropTypes.string,
    total: PropTypes.object,
    fees: PropTypes.arrayOf(PropTypes.object),
    deleteLoading: PropTypes.bool.isRequired,
    listings: PropTypes.arrayOf(PropTypes.object),
    hasCoupons: PropTypes.bool,
    hasAutoCoupons: PropTypes.bool,
    showSuccess: PropTypes.bool,
    coupon: PropTypes.object,
    isMultiEmbed: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(CartContainer)
